import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import DriverCountStepper from './driver-count-stepper.component'
import DriversPlanIcon from './drivers-plan-icon.component'
import {
  DEFAULT_DRIVER_UNIT_PRICE,
  formatCadMonthly,
} from 'store/subscriptions/subscriptions.types'

type UpgradeSubscriptionDialogProps = {
  open: boolean
  currentUnits: number
  numberOfDrivers?: number
  unitPrice?: number | null
  mode?: 'upgrade' | 'update' | 'reactivate'
  submitting?: boolean
  errorMessage?: string | null
  onClose: () => void
  onConfirm: (unit: number) => void
}

const pluralize = (count: number, word: string) =>
  count === 1 ? word : `${word}s`

const UpgradeSubscriptionDialog = ({
  open,
  currentUnits,
  numberOfDrivers = 0,
  unitPrice,
  mode = 'upgrade',
  submitting = false,
  errorMessage,
  onClose,
  onConfirm,
}: UpgradeSubscriptionDialogProps) => {
  const isUpdate = mode === 'update' || mode === 'reactivate'
  const isReactivate = mode === 'reactivate'
  const price = unitPrice && unitPrice > 0 ? unitPrice : DEFAULT_DRIVER_UNIT_PRICE
  const min = isUpdate ? 1 : Math.max(1, currentUnits) + 1
  const [unit, setUnit] = useState(isUpdate ? Math.max(1, currentUnits) : min)

  useEffect(() => {
    if (open) {
      setUnit(isUpdate ? Math.max(1, currentUnits) : Math.max(1, currentUnits) + 1)
    }
  }, [open, currentUnits, isUpdate])

  const delta = unit - currentUnits
  const total = unit * price
  const changedCount = Math.abs(delta)
  const driverWord = pluralize(changedCount, 'driver')
  const unchanged = !isReactivate && delta === 0
  const belowDriverCount = isUpdate && unit < numberOfDrivers
  const driverCountWord = pluralize(numberOfDrivers, 'driver')

  const helperText = isReactivate && delta === 0
    ? `Your plan will stay at ${unit} ${pluralize(unit, 'driver')} and billing will resume.`
    : unchanged
    ? `Your plan will stay at ${unit} ${pluralize(unit, 'driver')}.`
    : delta > 0
      ? `You will add ${delta} more ${driverWord} to your current plan.`
      : `You will remove ${changedCount} ${driverWord} from your current plan.`

  const heading = isReactivate
    ? 'Reactivate Your Driver Subscription'
    : isUpdate
      ? 'Update Your Driver Subscription'
      : 'Upgrade Your Driver Subscription'

  const noticeTitle = isReactivate
    ? `Your subscription will be reactivated with ${unit} ${pluralize(unit, 'driver')}.`
    : isUpdate
    ? `Your subscription will be updated to ${unit} ${pluralize(unit, 'driver')}.`
    : `Your subscription will be upgraded to ${unit} ${pluralize(unit, 'driver')}.`

  const noticeBody = isReactivate
    ? delta === 0
      ? 'Billing will continue from your next billing cycle. Your current driver count will stay the same until you change it.'
      : `Billing will continue from your next billing cycle. Your plan will include ${unit} ${pluralize(unit, 'driver')}.`
    : unchanged
    ? 'No change will be applied until you select a different number of drivers.'
    : delta > 0
      ? `Starting from your next billing cycle, you will be charged ${formatCadMonthly(total)}. The additional ${changedCount} ${driverWord} will be included in your plan.`
      : `Starting from your next billing cycle, you will be charged ${formatCadMonthly(total)}. Your plan will include ${changedCount} fewer ${driverWord}.`

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 4, position: 'relative' }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <DriversPlanIcon showPlus={delta >= 0} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#12355b">
              {heading}
            </Typography>
            <Typography mt={1} color="#6b7280">
              You currently have {currentUnits} drivers in your subscription.
              Select the new total number of drivers you need.
            </Typography>
          </Box>
          <Box width="100%">
            <Typography fontWeight={700} mb={1.5}>
              Total number of drivers
            </Typography>
            <DriverCountStepper value={unit} min={min} onChange={setUnit} />
            {belowDriverCount ? (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  gap: 1,
                  bgcolor: '#fdecec',
                  borderRadius: '10px',
                  p: 1.25,
                  textAlign: 'left',
                }}
              >
                <WarningAmberOutlinedIcon sx={{ color: '#dc2626', fontSize: 20, mt: '1px' }} />
                <Typography color="#991b1b" fontSize={13}>
                  Cannot be less than the number of drivers you have. You currently
                  have {numberOfDrivers} {driverCountWord}.
                </Typography>
              </Box>
            ) : (
              <Typography mt={1} color="#6b7280" fontSize={13}>
                {helperText}
              </Typography>
            )}
          </Box>
          <Box
            width="100%"
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              bgcolor: '#eef6fb',
              borderRadius: '12px',
              py: 2,
            }}
          >
            <Box px={1.5}>
              <Typography color="#6b7280" fontSize={13}>
                Price per driver
              </Typography>
              <Typography fontWeight={700} mt={0.5} fontSize={14}>
                {formatCadMonthly(price)}
              </Typography>
            </Box>
            <Box px={1.5} sx={{ borderLeft: '1px solid #d5e4ef' }}>
              <Typography color="#6b7280" fontSize={13}>
                Total drivers
              </Typography>
              <Typography fontWeight={700} mt={0.5} fontSize={18}>
                {unit}
              </Typography>
            </Box>
            <Box px={1.5} sx={{ borderLeft: '1px solid #d5e4ef' }}>
              <Typography color="#6b7280" fontSize={13}>
                Total amount (Monthly)
              </Typography>
              <Typography fontWeight={700} mt={0.5} fontSize={14}>
                {formatCadMonthly(total)}
              </Typography>
            </Box>
          </Box>
          <Box
            width="100%"
            sx={{
              display: 'flex',
              gap: 1.5,
              bgcolor: delta < 0 ? '#fef6e8' : '#e8f8ee',
              borderRadius: '12px',
              p: 1.5,
              textAlign: 'left',
            }}
          >
            <EventAvailableOutlinedIcon
              sx={{ color: delta < 0 ? '#d97706' : '#16a34a', mt: '2px' }}
            />
            <Box>
              <Typography
                fontWeight={700}
                color={delta < 0 ? '#92400e' : '#166534'}
                fontSize={14}
              >
                {noticeTitle}
              </Typography>
              <Typography color="#4b5563" fontSize={13} mt={0.5}>
                {noticeBody}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} width="100%">
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{ py: 1.2, borderColor: '#d1d5db' }}
            >
              Cancel
            </Button>
            <SubmitLockButton
              fullWidth
              variant="contained"
              disabled={unchanged || submitting || belowDriverCount}
              onClick={() => onConfirm(unit)}
              sx={{ py: 1.2 }}
            >
              {submitting
                ? 'Saving...'
                : isReactivate
                  ? 'Reactivate Subscription'
                  : isUpdate
                    ? 'Update Subscription'
                    : 'Upgrade Subscription'}
            </SubmitLockButton>
          </Stack>
          {errorMessage && (
            <Typography color="error" fontSize={14}>
              {errorMessage}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default UpgradeSubscriptionDialog
